import type {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from "react";

/**
 * A labelled text field: label, control, description, error. CSS only.
 * `multiline` makes a textarea; other attributes pass straight to the control.
 */
export function Input({
  id,
  label,
  description,
  error,
  hideLabel = false,
  multiline = false,
  rows = 4,
  className,
  density,
  value,
  ...attributes
}: Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "value"> & {
  id: string;
  name: string;
  label: string;
  description?: string;
  /** Shown under the field and announced with it; marks the field invalid. */
  error?: string;
  hideLabel?: boolean;
  multiline?: boolean;
  rows?: number;
  className?: string;
  /** Smaller desktop fields; touch screens keep 16px type and 44px targets. */
  density?: "compact";
  value?: string;
  /** The text control, for a page that clears or focuses it (a single-line field only). */
  ref?: Ref<HTMLInputElement>;
}) {
  const describedBy =
    [description && `${id}-description`, error && `${id}-error`].filter(Boolean).join(" ") ||
    undefined;
  return (
    <div
      className={["ui-field", className].filter(Boolean).join(" ")}
      data-ui="field"
      data-density={density}
      data-invalid={error ? "" : undefined}
    >
      <label htmlFor={id} className={hideLabel ? "ui-field-label sr-only" : "ui-field-label"}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          className="ui-input"
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={error ? "true" : undefined}
          {...(attributes as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          defaultValue={value ?? ""}
        />
      ) : (
        <input
          id={id}
          className="ui-input"
          defaultValue={value}
          aria-describedby={describedBy}
          aria-invalid={error ? "true" : undefined}
          {...attributes}
        />
      )}
      {description && (
        <p id={`${id}-description`} className="ui-field-description">
          {description}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="ui-field-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A checkbox, or a switch with `switch`, on the native input. CSS only. With `onChange` it is
 * controlled: `checked` is the state, so a restored form never disagrees with the page.
 */
export function Checkbox({
  id,
  name,
  label,
  description,
  value = "on",
  checked = false,
  disabled = false,
  required = false,
  switch: isSwitch = false,
  card = false,
  className,
  onChange,
}: {
  id: string;
  name: string;
  label: string;
  description?: string;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** An on/off setting that applies at once, rather than a choice submitted with a form. */
  switch?: boolean;
  /** Drawn as a selectable card, like RadioGroup's cards, for picking several of a few. */
  card?: boolean;
  className?: string;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <label
      className={["ui-check", className].filter(Boolean).join(" ")}
      data-ui="check"
      data-variant={isSwitch ? "switch" : "checkbox"}
      data-card={card ? "" : undefined}
    >
      <input
        id={id}
        className="ui-check-control"
        type="checkbox"
        role={isSwitch ? "switch" : undefined}
        name={name}
        value={value}
        {...(onChange
          ? {
              checked,
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                onChange(event.currentTarget.checked),
            }
          : { defaultChecked: checked })}
        disabled={disabled}
        required={required}
        aria-describedby={description ? `${id}-description` : undefined}
      />
      <span className="ui-check-text">
        <span className="ui-check-label">{label}</span>
        {description && (
          <span id={`${id}-description`} className="ui-check-description">
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

/**
 * One choice from a few, on native radios in a fieldset. CSS only; arrow keys
 * move between options natively. `cards` makes each option a selectable card;
 * `segmented` is one row of short options, like a lane filter. With `onChange`
 * it is controlled: `value` is the choice.
 */
export function RadioGroup({
  name,
  label,
  options,
  value,
  required = false,
  variant = "list",
  hideLabel = false,
  className,
  onChange,
}: {
  name: string;
  label: string;
  options: {
    value: string;
    label: string;
    ariaLabel?: string;
    description?: string;
    /** A small mark before the label, such as a lane icon in a segmented row. */
    icon?: ReactNode;
    disabled?: boolean;
  }[];
  value?: string;
  required?: boolean;
  variant?: "list" | "cards" | "segmented";
  hideLabel?: boolean;
  className?: string;
  onChange?: (value: string) => void;
}) {
  const choices = options.map((option) => (
    <label key={option.value} className="ui-radio">
      <input
        className="ui-radio-control"
        type="radio"
        name={name}
        value={option.value}
        aria-label={option.ariaLabel}
        {...(onChange
          ? {
              checked: option.value === value,
              onChange: () => onChange(option.value),
            }
          : { defaultChecked: option.value === value })}
        disabled={option.disabled}
        required={required}
      />
      <span className="ui-check-text">
        <span className="ui-radio-label">
          {option.icon}
          {option.icon ? <span className="ui-radio-text">{option.label}</span> : option.label}
        </span>
        {option.description && <span className="ui-radio-description">{option.description}</span>}
      </span>
    </label>
  ));
  return (
    <fieldset
      className={["ui-radio-group", className].filter(Boolean).join(" ")}
      data-ui="radio-group"
      data-variant={variant}
    >
      <legend className={hideLabel ? "ui-radio-legend sr-only" : "ui-radio-legend"}>{label}</legend>
      {/* A shown label sits above a segmented row, so the row draws its own track. */}
      {variant === "segmented" && !hideLabel ? (
        <div className="ui-radio-track">{choices}</div>
      ) : (
        choices
      )}
    </fieldset>
  );
}
